import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Alert, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, IconButton, CircularProgress } from '@mui/material';
import { PersonAdd, Edit, Delete, Refresh } from '@mui/icons-material';
import axios from 'axios';
import api from '../services/api';


interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  createdAt: string;
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'EMPLOYEE',
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
        const response = await api.get('/users');
      setUsers(response.data.data || []);
    } catch (error: any) {
      console.error('Error fetching users:', error);
      setError(error.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        role: user.role,
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'EMPLOYEE',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'EMPLOYEE',
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token');
      
      if (editingUser) {
        // Update user
        await axios.put(
            `https://hst-gst-tracker.fareedsheikllp.com/api/users/${editingUser.id}`,
          {
            name: formData.name,
            email: formData.email,
            role: formData.role,
            ...(formData.password && { password: formData.password }),
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess('User updated successfully!');
      } else {
        // Create user
        await axios.post(
            'https://hst-gst-tracker.fareedsheikllp.com/api/users',
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess('User created successfully!');
      }

      handleCloseDialog();
      fetchUsers();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Error saving user:', error);
      setError(error.response?.data?.message || 'Failed to save user');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const token = localStorage.getItem('token');
        await axios.delete(`https://hst-gst-tracker.fareedsheikllp.com/api/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
        });
      setSuccess('User deleted successfully!');
      fetchUsers();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Error deleting user:', error);
      setError(error.response?.data?.message || 'Failed to delete user');
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'SUPER_ADMIN':
        return { bg: '#FEE2E2', text: '#991B1B', border: '#FCA5A5' };
      case 'ADMIN':
        return { bg: '#DBEAFE', text: '#1E40AF', border: '#93C5FD' };
      case 'EMPLOYEE':
        return { bg: '#D1FAE5', text: '#065F46', border: '#6EE7B7' };
      default:
        return { bg: '#F3F4F6', text: '#374151', border: '#D1D5DB' };
    }
  };

  const formatRole = (role: string) => {
    switch (role) {
      case 'SUPER_ADMIN':
        return 'Super Admin';
      case 'ADMIN':
        return 'Admin';
      case 'EMPLOYEE':
        return 'Employee';
      default:
        return role;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#667eea' }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, fontSize: '2rem', mb: 1, color: '#1a1a1a' }}>
            User Management
          </Typography>
          <Typography sx={{ color: '#666666', fontSize: '1rem' }}>
            Create and manage employee access to the system
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            startIcon={<Refresh />}
            onClick={fetchUsers}
            sx={{
              textTransform: 'none',
              borderColor: '#667eea',
              color: '#667eea',
              fontWeight: 600,
              px: 3,
              py: 1.25,
              border: '2px solid',
              '&:hover': { 
                borderColor: '#5568d3',
                backgroundColor: '#F9FAFB'
              },
            }}
          >
            Refresh
          </Button>
          <Button
            startIcon={<PersonAdd />}
            onClick={() => handleOpenDialog()}
            sx={{
              textTransform: 'none',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#FFFFFF',
              fontWeight: 600,
              px: 3,
              py: 1.25,
              '&:hover': { background: 'linear-gradient(135deg, #5568d3 0%, #63408a 100%)' },
            }}
          >
            Add User
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Users Table */}
      <Paper sx={{ border: '1px solid #E5E7EB', borderRadius: 3, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }} elevation={0}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
                <TableCell sx={{ fontWeight: 700, color: '#374151', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: 'center', py: 6, color: '#6B7280' }}>
                    No users found. Click "Add User" to create one.
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => {
                  const roleColors = getRoleColor(user.role);
                  return (
                    <TableRow 
                      key={user.id}
                      sx={{ 
                        '&:hover': { backgroundColor: '#F9FAFB' },
                        borderBottom: '1px solid #F3F4F6'
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
                          {user.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.9375rem' }}>
                          {user.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={formatRole(user.role)}
                          size="small"
                          sx={{
                            backgroundColor: roleColors.bg,
                            color: roleColors.text,
                            border: `1px solid ${roleColors.border}`,
                            fontWeight: 700,
                            fontSize: '0.75rem',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.9375rem' }}>
                          {new Date(user.createdAt).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(user)}
                            sx={{ 
                              color: '#667eea',
                              '&:hover': { backgroundColor: '#F3F4F6' }
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteUser(user.id)}
                            sx={{ 
                              color: '#EF4444',
                              '&:hover': { backgroundColor: '#FEE2E2' }
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit User Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.5rem', color: '#1a1a1a', pb: 2 }}>
          {editingUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
              <TextField
                label="Full Name"
                required
                fullWidth
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: '#667eea' },
                    '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' },
                  },
                }}
              />

              <TextField
                label="Email"
                type="email"
                required
                fullWidth
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: '#667eea' },
                    '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' },
                  },
                }}
              />

              <TextField
                label="Password"
                type="password"
                required={!editingUser}
                fullWidth
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                helperText={editingUser ? 'Leave blank to keep current password' : ''}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: '#667eea' },
                    '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' },
                  },
                }}
              />

              <TextField
                select
                label="Role"
                required
                fullWidth
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: '#667eea' },
                    '&.Mui-focused fieldset': { borderColor: '#667eea', borderWidth: '2px' },
                  },
                }}
              >
                <MenuItem value="SUPER_ADMIN">Super Admin</MenuItem>
                <MenuItem value="ADMIN">Admin</MenuItem>
                <MenuItem value="EMPLOYEE">Employee</MenuItem>
              </TextField>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button
              onClick={handleCloseDialog}
              sx={{ textTransform: 'none', color: '#6B7280', fontWeight: 600, px: 3 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              sx={{
                textTransform: 'none',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#FFFFFF',
                fontWeight: 600,
                px: 4,
                '&:hover': { background: 'linear-gradient(135deg, #5568d3 0%, #63408a 100%)' },
              }}
            >
              {editingUser ? 'Update User' : 'Create User'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default UserManagement;